
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import configs from "../../configs/config";

import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Box,
} from "@mui/material";
import { getSubDomainName } from "../helpers/helperFunctions";
import { getTenantBySubDomain } from "../../redux/actions/Login/Login";
import { triggerAppAlert } from "../../redux/actions/SelfServicePortal/ContactAid/ContactAid";
import { useDispatch } from "react-redux";

const WebFormValues = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<any>(null);

  const ticketId = searchParams.get("ticketId");
  const ticketLogId = searchParams.get("ticketLogId");

  useEffect(() => {
    if (!ticketId || !ticketLogId) {
      setError("Invalid URL: ticketId or ticketLogId missing.");
      setLoading(false);
      return;
    }

    const fetchWebformValues = async () => {
      try {
        const subDomain = getSubDomainName();
        const tenantRes = await axios(`${getTenantBySubDomain}${subDomain}`);
        const url = `${configs.BASE_URL}/common/WebformValues`;
        const response = await axios.get(url, {
          params: { ticketId, ticketLogId },
          headers: { "tenant-code": tenantRes.data.tenantcode },
        });

        const data = response?.data;

        let parsedValues: any = {};

        if (data?.webformValuesJson) {
          let arr = JSON.parse(data.webformValuesJson);

          if(arr?.[0]?.isNew){ // enhancement for webfrom for backward compatibility
            arr = JSON.parse(arr[1].webformValues).map(({fieldLabel,value}: { fieldLabel: string; value: any })=>{
                return {
                    [fieldLabel] : value
                }
            })
          }

          if (Array?.isArray(arr)) {
            parsedValues = arr?.reduce((acc: any, item: any) => {
              const [key, value] = Object?.entries(item)[0];
              acc[key] = value;
              return acc;
            }, {});
          } else {
            parsedValues = arr; // fallback
          }
        }else if(!data?.webformId || data?.webformId == 0){
          dispatch(triggerAppAlert(`No webform values found for this ticket - ${ticketId}.`, "warning"));
          // navigate to 404 page or show a message to the user
          navigate("/404"); // Assuming you have a 404 route set up
          return;
        }

        setFormValues({
          ...data,
          webformValuesJson: parsedValues,
        });
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWebformValues();
  }, [ticketId, ticketLogId]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress size={50} />
      </Box>
    );

  if (error)
    return (
      <Box m={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  return (
    <Box sx={{ maxWidth: 800, margin: "40px auto" }}>
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Webform Details
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box display="flex" flexDirection="column" rowGap={1}>
            <Typography>
              <strong>Ticket ID:</strong> {formValues?.ticketId}
            </Typography>
          </Box>

          <Box sx={{ p: 2, borderRadius: 2 }}>
            {Object.entries(formValues?.webformValuesJson || {}).map(
              ([key, value]: any) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #eee",
                    py: 1,
                  }}
                >
                  <Typography fontWeight={600} sx={{ color: "#444" }}>
                    {key}
                  </Typography>

                  <Typography sx={{ ml: 2, color: "#555" }}>
                    {String(value)}
                  </Typography>
                </Box>
              )
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WebFormValues;
